import React from 'react';
import PropTypes from 'prop-types';
import { LuInfo, LuSearch, LuTriangleAlert } from 'react-icons/lu';
import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useSmoText from '../smoText';
import { ChartCard, useChartName, useChartTheme } from './chartKit';
import { CLICKABLE, HERO_TONES, TEXT_TONES, issueRateBadge, rateTone } from './tones';

/** Big metric card; clickable when it has visits to inspect. */
export const HeroCard = ({ tone, value, title, sub, onClick, hint }) => {
  const content = (
    <>
      <span className="tw-block tw-text-3xl tw-font-bold tw-leading-none tw-tracking-tight tw-tabular-nums">{value}</span>
      <span className="tw-mt-2 tw-block tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wide">{title}</span>
      <span className="tw-mt-1 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-xs tw-opacity-80">
        {sub}
        {onClick && <LuSearch aria-hidden="true" className="tw-h-3 tw-w-3" />}
      </span>
    </>
  );
  const base = `tw-rounded-2xl tw-border tw-p-4 tw-text-center ${HERO_TONES[tone]}`;
  return onClick
    ? <button type="button" onClick={onClick} title={hint} className={`${base} ${CLICKABLE}`}>{content}</button>
    : <div className={base}>{content}</div>;
};

HeroCard.propTypes = {
  tone: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  sub: PropTypes.node,
  onClick: PropTypes.func,
  hint: PropTypes.string,
};

export const ALERT_TONES = {
  danger: 'tw-border-rose-500/30 tw-bg-rose-500/[0.07] tw-text-rose-800 dark:tw-border-rose-400/25 dark:tw-text-rose-200',
  warning: 'tw-border-amber-500/30 tw-bg-amber-400/[0.1] tw-text-amber-800 dark:tw-border-amber-400/25 dark:tw-text-amber-200',
  info: 'tw-border-sky-500/30 tw-bg-sky-500/[0.07] tw-text-sky-800 dark:tw-border-sky-400/25 dark:tw-text-sky-200',
};

/** Small tile of the cycle-time card. */
const CycleTile = ({ value, title, toneClass, onClick, hint }) => {
  const content = (
    <>
      <span className={`tw-block tw-text-xl tw-font-bold tw-tabular-nums ${toneClass || 'tw-text-slate-900 dark:tw-text-white'}`}>{value}</span>
      <span className="tw-mt-1 tw-block tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400">{title}</span>
    </>
  );
  const base = 'tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-3 tw-text-center dark:tw-border-white/10 dark:tw-bg-white/[0.03]';
  return onClick
    ? <button type="button" onClick={onClick} title={hint} className={`${base} ${CLICKABLE}`}>{content}</button>
    : <div className={base}>{content}</div>;
};

CycleTile.propTypes = { value: PropTypes.node.isRequired, title: PropTypes.string.isRequired, toneClass: PropTypes.string, onClick: PropTypes.func, hint: PropTypes.string };

/** Body of "Operasyonel Genel Bakış" (Section 1). */
const OperationalOverview = ({ summary, visits, capaDashboard, openIssueModal, filterVisitsByMetric }) => {
  const { label, locale } = useSmoText();
  const chart = useChartTheme();
  const chartName = useChartName();
  const detailHint = label('common.detailHint');
  const days = (value) => (value !== null ? label('common.days', { value }) : '—');

  return (
    <div className="tw-space-y-4">
      <p className="tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
        <LuInfo aria-hidden="true" className="tw-mt-px tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-sky-500" />
        {label('overview.clickHint')}
      </p>

      {/* ── Hero Metric Cards ── */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3 xl:tw-grid-cols-5">
        <HeroCard tone="blue" value={summary.total} title={label('overview.totalVisits')} sub={label('overview.homeSite', { home: summary.homeCount, site: summary.siteCount })} />
        <HeroCard
          tone={rateTone(summary.qualityRate, 90, 70)}
          value={`%${summary.qualityRate}`}
          title={label('overview.qualityRate')}
          sub={label('overview.issueVisits', { count: summary.issueCount })}
          onClick={summary.issueCount > 0 ? () => openIssueModal(label('issue.issues'), visits, 'issues') : undefined}
          hint={label('overview.issueHint')}
        />
        <HeroCard
          tone={rateTone(summary.slaRate, 80, 50)}
          value={`%${summary.slaRate}`}
          title={label('overview.slaRate')}
          sub={label('overview.overdueVisits', { count: summary.overdueCount })}
          onClick={summary.overdueCount > 0 ? () => openIssueModal(label('issue.overdue'), visits, 'overdue') : undefined}
          hint={label('overview.overdueHint')}
        />
        <HeroCard tone={rateTone(summary.docRate, 80, 50)} value={`%${summary.docRate}`} title={label('overview.docRate')} sub={label('overview.docSub')} />
        <HeroCard
          tone="purple"
          value={summary.totalKm > 0 ? summary.totalKm.toLocaleString(locale) : '—'}
          title={label('overview.totalKm')}
          sub={label('overview.kmSub')}
        />
      </div>

      {/* ── Alerts Strip ── */}
      {summary.alerts.length > 0 && (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {summary.alerts.map((alert) => {
            const clickable = !!alert.metric && filterVisitsByMetric(visits, alert.metric).length > 0;
            const text = label(`alerts.${alert.metric}`, alert.vars);
            const className = `tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-px-3 tw-py-2 tw-text-xs tw-font-semibold ${ALERT_TONES[alert.type]}`;
            return clickable ? (
              <button key={alert.metric} type="button" onClick={() => openIssueModal(text, visits, alert.metric)} title={detailHint} className={`${className} ${CLICKABLE}`}>
                <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4" />
                {text}
                <LuSearch aria-hidden="true" className="tw-h-3 tw-w-3 tw-opacity-60" />
              </button>
            ) : (
              <span key={alert.metric} className={className}>
                <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4" />
                {text}
              </span>
            );
          })}
        </div>
      )}

      {/* ── Ev vs Hastane Karşılaştırma ── */}
      {summary.comparisonChart.length > 0 && (
        <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
          <ChartCard title={label('overview.comparison')}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.comparisonChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={chart.tick} tickFormatter={chartName} />
                <YAxis unit="%" domain={[0, 100]} tick={chart.tick} />
                <Tooltip formatter={(v) => `${v}%`} labelFormatter={chartName} {...chart.tooltip} />
                <Legend wrapperStyle={chart.legend} />
                <Bar dataKey="Ev" name={chartName('Ev')} fill="#4299e1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Hastane" name={chartName('Hastane')} fill="#9f7aea" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Cycle Time Summary Card */}
          <ChartCard title={label('overview.cycle')}>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              <CycleTile value={days(summary.avgCycleHome)} title={label('overview.cycleHome')} />
              <CycleTile value={days(summary.avgCycleSite)} title={label('overview.cycleSite')} />
              <CycleTile
                value={summary.missingProcessEnd}
                title={label('overview.missingProcessEnd')}
                toneClass={summary.missingProcessEnd > 0 ? TEXT_TONES.warn : TEXT_TONES.ok}
                onClick={summary.missingProcessEnd > 0 ? () => openIssueModal(label('issue.missingProcessEnd'), visits, 'missingProcessEnd') : undefined}
                hint={detailHint}
              />
              <CycleTile
                value={summary.missedCount}
                title={label('overview.missed')}
                toneClass={summary.missedCount > 0 ? TEXT_TONES.bad : TEXT_TONES.ok}
                onClick={summary.missedCount > 0 ? () => openIssueModal(label('issue.missed'), visits, 'missed') : undefined}
                hint={detailHint}
              />
            </div>
            <p className="tw-mt-3 tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{label('overview.cycleHelp')}</p>
          </ChartCard>
        </div>
      )}

      {/* ── Monthly Trend & Pie Charts ── */}
      {(summary.monthlyTrend.length > 1 || (capaDashboard && capaDashboard.pieChartData.length > 0)) && (
        <div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
          {summary.monthlyTrend.length > 1 && (
            <ChartCard title={label('overview.monthlyTrend')}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={summary.monthlyTrend} margin={{ bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="month" tick={chart.tick} />
                  <YAxis allowDecimals={false} tick={chart.tick} />
                  <Tooltip {...chart.tooltip} />
                  <Legend wrapperStyle={chart.legend} />
                  <Bar dataKey="Ev" name={chartName('Ev')} stackId="visits" fill="#4299e1" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Hastane" name={chartName('Hastane')} stackId="visits" fill="#9f7aea" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="Sorun" name={chartName('Sorun')} stroke="#fc8181" strokeWidth={2} dot={{ r: 4 }} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {capaDashboard && capaDashboard.pieChartData.length > 0 && (
            <ChartCard title={label('overview.qualityPie')}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={capaDashboard.pieChartData.map((entry) => ({ ...entry, shown: chartName(entry.name) }))}
                    dataKey="value"
                    nameKey="shown"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    label={({ shown, value }) => `${shown}: ${value}`}
                  >
                    {capaDashboard.pieChartData.map((entry) => {
                      const colorMap = { 'Sorunlu Vizit': '#fc8181', 'Sorunsuz Vizit': '#68d391' };
                      return <Cell key={entry.name} fill={colorMap[entry.name] || '#a0aec0'} />;
                    })}
                  </Pie>
                  <Tooltip {...chart.tooltip} />
                  <Legend wrapperStyle={chart.legend} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </div>
      )}

      {/* ── Per-Coordinator Breakdown ── */}
      {capaDashboard && capaDashboard.coordinators.length > 0 && (
        <CoordinatorTable capaDashboard={capaDashboard} openIssueModal={openIssueModal} />
      )}
    </div>
  );
};

OperationalOverview.propTypes = {
  summary: PropTypes.object.isRequired,
  visits: PropTypes.array.isRequired,
  capaDashboard: PropTypes.object,
  openIssueModal: PropTypes.func.isRequired,
  filterVisitsByMetric: PropTypes.func.isRequired,
};

const CELL = 'tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-text-center tw-tabular-nums';

/** Count cell that opens the issue details when it is above zero. */
const CountCell = ({ value, toneClass, bold, onClick }) => (
  <td className={CELL}>
    {value > 0 && onClick ? (
      <button type="button" onClick={onClick} className={`tw-rounded-lg tw-bg-transparent tw-px-2 tw-py-0.5 tw-underline tw-decoration-dotted tw-underline-offset-4 hover:tw-bg-slate-900/[0.05] dark:hover:tw-bg-white/[0.06] ${toneClass || ''} ${bold ? 'tw-font-bold' : ''}`}>
        {value}
      </button>
    ) : value}
  </td>
);

CountCell.propTypes = { value: PropTypes.number.isRequired, toneClass: PropTypes.string, bold: PropTypes.bool, onClick: PropTypes.func };

/** Koordinatör Bazlı Ev Viziti Dağılımı. */
const CoordinatorTable = ({ capaDashboard, openIssueModal }) => {
  const { label } = useSmoText();
  const { totals } = capaDashboard;
  const totalRate = totals.totalVisits > 0 ? Math.round(totals.totalIssues / totals.totalVisits * 100) : 0;
  let totalBadge = 'tw-bg-slate-500/10 tw-text-slate-500';
  if (totals.totalVisits > 0) totalBadge = totalRate <= 10 ? 'tw-bg-emerald-500/15 tw-text-emerald-800 dark:tw-text-emerald-200' : 'tw-bg-amber-400/20 tw-text-amber-800 dark:tw-text-amber-200';

  return (
    <ChartCard title={label('overview.coordinatorTable')}>
      <div className="tw-relative tw-overflow-x-auto glass-scrollbar">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead>
            <tr className="tw-border-b tw-border-slate-200/80 dark:tw-border-white/10">
              {['coordinator', 'homeVisits', 'issues', 'missed', 'aesae', 'dcf', 'issueRate'].map((key, i) => (
                <th key={key} scope="col" className={`tw-whitespace-nowrap tw-px-3 tw-py-2 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400 ${i === 0 ? 'tw-text-left' : 'tw-text-center'}`}>
                  {label(`coordinators.${key}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-slate-200/70 tw-text-slate-700 dark:tw-divide-white/[0.06] dark:tw-text-slate-200">
            {capaDashboard.coordinators.map((c) => {
              const issueRate = c.totalVisits > 0 ? Math.round(c.totalIssues / c.totalVisits * 100) : 0;
              return (
                <tr key={c.id}>
                  <td className="tw-whitespace-nowrap tw-px-3 tw-py-2.5 tw-font-semibold tw-text-slate-900 dark:tw-text-white">{c.name}</td>
                  <td className={CELL}>{c.totalVisits}</td>
                  <CountCell value={c.totalIssues} toneClass={TEXT_TONES.warn} onClick={() => openIssueModal(label('issue.personIssues', { name: c.name }), c.visits, 'issues')} />
                  <CountCell value={c.missedVisits} toneClass={TEXT_TONES.bad} bold onClick={() => openIssueModal(label('issue.personMissed', { name: c.name }), c.visits, 'missed')} />
                  <CountCell value={c.aeSaeCount} onClick={() => openIssueModal(`${c.name} — AE/SAE`, c.visits, 'aesae')} />
                  <CountCell value={c.dcfCreatedCount} onClick={() => openIssueModal(`${c.name} — DCF`, c.visits, 'dcf')} />
                  <td className={CELL}>
                    <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${issueRateBadge(issueRate)}`}>%{issueRate}</span>
                  </td>
                </tr>
              );
            })}
            <tr className="tw-bg-slate-50/80 tw-font-bold dark:tw-bg-white/[0.03]">
              <td className="tw-px-3 tw-py-2.5 tw-text-slate-900 dark:tw-text-white">{label('coordinators.total')}</td>
              <td className={CELL}>{totals.totalVisits}</td>
              <td className={`${CELL} ${TEXT_TONES.warn}`}>{totals.totalIssues}</td>
              <td className={`${CELL} ${TEXT_TONES.bad}`}>{totals.missedVisits}</td>
              <td className={CELL}>{totals.aeSaeCount}</td>
              <td className={CELL}>{totals.dcfCreatedCount}</td>
              <td className={CELL}><span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs ${totalBadge}`}>%{totalRate}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
};

CoordinatorTable.propTypes = { capaDashboard: PropTypes.object.isRequired, openIssueModal: PropTypes.func.isRequired };

export default OperationalOverview;
